import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  Module,
  ModuleCategory,
  CreateModuleDto,
  UpdateModuleDto,
  CreateModuleCategoryDto,
  UpdateModuleCategoryDto,
  FindModulesDto,
  ModulesState,
  PaginatedResponse,
} from '../types/module.types';

// Tipos para las acciones
export interface CreateCategoryRequest {
  data: CreateModuleCategoryDto;
}

export interface UpdateCategoryRequest {
  id: string;
  data: UpdateModuleCategoryDto;
}

export interface DeleteCategoryRequest {
  id: string;
}

// Estado inicial para módulos
const initialModulesState: ModulesState = {
  modules: [],
  categories: [],
  currentModule: null,
  currentCategory: null,
  loading: false,
  error: null,
  pagination: {
    page: 1,
    limit: 10,
    total: 0,
  },
  filters: {
    search: '',
    categoryId: '',
    status: '',
    visibility: '',
    isRequired: null,
    isAddon: null,
  },
};

// Función helper para asegurar que categories sea un array
const ensureCategoriesArray = (state: any) => {
  if (!Array.isArray(state.categories)) {
    state.categories = [];
  }
};

// Acciones síncronas para módulos (las sagas manejan la lógica asíncrona)
export const fetchModules = (query: FindModulesDto = {}) => ({ 
  type: 'modules/fetchModules', 
  payload: query 
});
export const fetchModule = (id: string) => ({ 
  type: 'modules/fetchModule', 
  payload: { id } 
});
export const createModule = (data: CreateModuleDto) => ({ 
  type: 'modules/createModule', 
  payload: { data } 
});
export const updateModule = (id: string, data: UpdateModuleDto) => ({ 
  type: 'modules/updateModule', 
  payload: { id, data } 
});
export const deleteModule = (id: string) => ({ 
  type: 'modules/deleteModule', 
  payload: { id } 
});
export const duplicateModule = (id: string) => ({ 
  type: 'modules/duplicateModule', 
  payload: { id } 
});
export const toggleModuleStatus = (id: string) => ({ 
  type: 'modules/toggleModuleStatus', 
  payload: { id } 
});

// Acciones síncronas para categorías (las sagas manejan la lógica asíncrona)
export const fetchCategories = () => ({ type: 'modules/fetchCategories' });
export const fetchCategory = (id: string) => ({ type: 'modules/fetchCategory', payload: { id } });
export const createCategory = (data: CreateModuleCategoryDto) => ({ 
  type: 'modules/createCategory', 
  payload: { data } 
});
export const updateCategory = (id: string, data: UpdateModuleCategoryDto) => ({ 
  type: 'modules/updateCategory', 
  payload: { id, data } 
});
export const deleteCategory = (id: string) => ({ 
  type: 'modules/deleteCategory', 
  payload: { id } 
});

// Las acciones de éxito y error están definidas en los reducers del slice

// Slice para módulos
const modulesSlice = createSlice({
  name: 'modules',
  initialState: initialModulesState,
  reducers: {
    setFilters: (
      state,
      action: PayloadAction<Partial<ModulesState['filters']>>
    ) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    clearFilters: state => {
      state.filters = {
        search: '',
        categoryId: '',
        status: '',
        visibility: '',
        isRequired: null,
        isAddon: null,
      };
    },
    setCurrentModule: (state, action: PayloadAction<Module | null>) => {
      state.currentModule = action.payload;
    },
    setCurrentCategory: (
      state,
      action: PayloadAction<ModuleCategory | null>
    ) => {
      state.currentCategory = action.payload;
    },
    clearError: state => {
      state.error = null;
    },
    // Reducers para módulos
    fetchModulesStart: state => {
      state.loading = true;
      state.error = null;
    },
    fetchModulesSuccess: (state, action: PayloadAction<PaginatedResponse<Module>>) => {
      state.loading = false;
      state.modules = action.payload.data || [];
      state.pagination = {
        page: action.payload.page || 1,
        limit: action.payload.limit || 10,
        total: action.payload.total || 0,
      };
    },
    createModuleSuccess: (state, action: PayloadAction<Module>) => {
      state.loading = false;
      state.modules.push(action.payload);
    },
    fetchModuleSuccess: (state, action: PayloadAction<Module>) => {
      state.loading = false;
      state.currentModule = action.payload;
    },
    updateModuleSuccess: (state, action: PayloadAction<Module>) => {
      state.loading = false;
      const index = state.modules.findIndex(module => module.id === action.payload.id);
      if (index !== -1) {
        state.modules[index] = action.payload;
      }
      if (state.currentModule?.id === action.payload.id) {
        state.currentModule = action.payload;
      }
    },
    // Reducers para categorías
    fetchCategoriesStart: state => {
      state.loading = true;
      state.error = null;
    },
    fetchCategoriesSuccess: (state, action: PayloadAction<ModuleCategory[]>) => {
      state.loading = false;
      state.categories = Array.isArray(action.payload) ? action.payload : [];
    },
    fetchCategoryStart: state => {
      state.loading = true;
      state.error = null;
    },
    fetchCategorySuccess: (state, action: PayloadAction<ModuleCategory>) => {
      state.loading = false;
      state.currentCategory = action.payload;
    },
    createCategoryStart: state => {
      state.loading = true;
      state.error = null;
    },
    createCategorySuccess: (state, action: PayloadAction<ModuleCategory>) => {
      state.loading = false;
      
      // Asegurar que categories sea un array
      ensureCategoriesArray(state);
      
      state.categories.push(action.payload);
    },
    updateCategoryStart: state => {
      state.loading = true;
      state.error = null;
    },
    updateCategorySuccess: (state, action: PayloadAction<ModuleCategory>) => {
      state.loading = false;
      const index = state.categories.findIndex(
        category => category.id === action.payload.id
      );
      if (index !== -1) {
        state.categories[index] = action.payload;
      }
      if (state.currentCategory?.id === action.payload.id) {
        state.currentCategory = action.payload;
      }
    },
    deleteCategoryStart: state => {
      state.loading = true;
      state.error = null;
    },
    deleteCategorySuccess: (state, action: PayloadAction<string>) => {
      state.loading = false;
      state.categories = state.categories.filter(
        category => category.id !== action.payload
      );
      if (state.currentCategory?.id === action.payload) {
        state.currentCategory = null;
      }
    },
    modulesFailure: (state, action: PayloadAction<string>) => {
      state.loading = false;
      state.error = action.payload;
    },
  },
});

export const {
  setFilters,
  clearFilters,
  setCurrentModule,
  setCurrentCategory,
  clearError,
  fetchModulesStart,
  fetchModulesSuccess,
  createModuleSuccess,
  fetchModuleSuccess,
  updateModuleSuccess,
  fetchCategoriesStart,
  fetchCategoriesSuccess,
  fetchCategoryStart,
  fetchCategorySuccess,
  createCategoryStart,
  createCategorySuccess,
  updateCategoryStart,
  updateCategorySuccess,
  deleteCategoryStart,
  deleteCategorySuccess,
  modulesFailure,
} = modulesSlice.actions;

export default modulesSlice.reducer;
